// src/runtime/shared/vaultHotswap.ts
// ============================================
// 企业大脑（Obsidian Vault）热插拔配置
//
// 商业化场景：
//   为不同企业部署 XEAOS 时，无需修改代码，只需：
//     1. 服务器端：通过 /vault-api/switch-vault 更换 Vault 根路径（企业知识大脑）
//     2. 前端：配置 Obsidian 连接（URL/API Key）与「大脑 Runtime」选择
//   更换企业 = 更换大脑（Vault），其余系统零改动。
//
// 存储：localStorage（浏览器），键名 xeaos_vault_hotswap
// ============================================

export interface VaultHotswapConfig {
  /** 服务器 Vault 根路径（由 /vault-api/switch-vault 维护；前端仅展示） */
  serverVaultRoot: string;
  /** Obsidian Local REST API URL（企业自有 Obsidian 时配置） */
  obsidianUrl: string;
  /** Obsidian Local REST API Key */
  obsidianApiKey: string;
  /** 是否优先使用 MCP（Obsidian）读取 */
  preferMcp: boolean;
  /** 大脑 Runtime（企业总脑）选择：enterprise_brain / business_operator / hermes */
  brainRuntime: string;
  /** 企业名称（仅用于展示标识） */
  enterpriseName: string;
  /** 最后更新时间 */
  updatedAt: string;
}

const STORAGE_KEY = "xeaos_vault_hotswap";

const DEFAULT_CONFIG: VaultHotswapConfig = {
  serverVaultRoot: "",
  obsidianUrl: "",
  obsidianApiKey: "",
  preferMcp: false,
  brainRuntime: "enterprise_brain",
  enterpriseName: "",
  updatedAt: new Date().toISOString(),
};

class VaultHotswapManager {
  private config: VaultHotswapConfig = { ...DEFAULT_CONFIG };
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.load();
  }

  private load() {
    try {
      if (typeof localStorage === "undefined") return;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.config = { ...DEFAULT_CONFIG, ...JSON.parse(stored) };
      }
    } catch {
      this.config = { ...DEFAULT_CONFIG };
    }
  }

  private save() {
    try {
      if (typeof localStorage === "undefined") return;
      this.config.updatedAt = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
      this.notify();
    } catch {
      /* localStorage 不可用时仅内存生效 */
    }
  }

  get(): VaultHotswapConfig {
    return { ...this.config };
  }

  update(partial: Partial<VaultHotswapConfig>): void {
    this.config = { ...this.config, ...partial };
    this.save();
  }

  reset(): void {
    this.config = { ...DEFAULT_CONFIG, updatedAt: new Date().toISOString() };
    this.save();
  }

  /** 订阅配置变更 */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }
}

/** 单例 */
export const vaultHotswap = new VaultHotswapManager();

/** 获取当前大脑 Runtime（带默认值） */
export function getBrainRuntime(): string {
  const cfg = vaultHotswap.get();
  return cfg.brainRuntime || "enterprise_brain";
}

export default vaultHotswap;
